import React, { Component, Suspense, lazy, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';

const SLOW_LOADING_NOTICE_MS = 20000;
const LOAD_FAILURE_NOTICE_MS = 60000;
const refreshPage = () => window.location.reload();

function PageLoading({ label, LoadingFallback, onRetry }) {
  const [takingLonger, setTakingLonger] = useState(false);
  const [loadTimedOut, setLoadTimedOut] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setTakingLonger(true), SLOW_LOADING_NOTICE_MS);
    const timeout = setTimeout(() => setLoadTimedOut(true), LOAD_FAILURE_NOTICE_MS);
    return () => {
      clearTimeout(timer);
      clearTimeout(timeout);
    };
  }, []);

  return (
    <Box role="status" sx={{ maxWidth: LoadingFallback ? 1100 : 640, mx: 'auto', px: 2, py: 8, textAlign: 'center' }}>
      {!LoadingFallback && <CircularProgress size={28} aria-hidden="true" />}
      <Typography sx={{ mt: 2 }}>Loading {label}...</Typography>
      {LoadingFallback && <LoadingFallback />}
      {takingLonger && !loadTimedOut && (
        <>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading is taking longer than expected. You can keep waiting or refresh the page.
          </Typography>
          <Button variant="outlined" onClick={refreshPage} sx={{ mt: 2 }}>Refresh page</Button>
        </>
      )}
      {loadTimedOut && (
        <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
          <Typography variant="body2">
            The builder is still unavailable after a minute. The download may be blocked or the published file may be unavailable.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button variant="contained" onClick={onRetry}>Try loading again</Button>
            <Button variant="outlined" onClick={refreshPage}>Refresh page</Button>
          </Stack>
        </Alert>
      )}
    </Box>
  );
}

class PageErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <Box sx={{ maxWidth: 640, mx: 'auto', px: 2, py: 8 }}>
        <Alert severity="error">
          <Typography component="h1" variant="h6">This page could not load</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Check your connection and try again. If the problem continues, refresh the page to get the latest version of the site.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button variant="contained" onClick={this.props.onRetry}>Try again</Button>
            <Button variant="outlined" onClick={refreshPage}>Refresh page</Button>
          </Stack>
        </Alert>
      </Box>
    );
  }
}

// Each route gets a stable lazy component. Retrying creates a fresh instance
// because React.lazy caches rejected imports as well as successful imports.
export const createLazyPage = (load, label, LoadingFallback) => {
  const createAttempt = (number) => ({
    number,
    Page: lazy(() => Promise.resolve().then(load)),
  });

  function LazyPage(props) {
    const [attempt, setAttempt] = useState(() => createAttempt(0));
    const { Page } = attempt;

    return (
      <PageErrorBoundary
        key={attempt.number}
        onRetry={() => setAttempt(current => createAttempt(current.number + 1))}
      >
        <Suspense fallback={<PageLoading label={label} LoadingFallback={LoadingFallback} onRetry={() => setAttempt(current => createAttempt(current.number + 1))} />}>
          <Page {...props} />
        </Suspense>
      </PageErrorBoundary>
    );
  }

  return LazyPage;
};
